import { ChangeEvent, memo } from 'react';

import { cx } from '@emotion/css';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  FormParam,
  useRSubmitApplication,
  useRGetSavedApplication,
  SavedApplicationDataResponse,
} from '@repositories/new-application';
import TncDialog from './components/TncDialog';
import ApplicationCard from '../../application-card';
import ReviewAccordion from './components/ReviewAccordion';
import { Button, Checkbox, useSnackbar } from '@new-components/elements';

import useGlobalState from 'actions';
import { excludeKey } from '@utils/object';
import { defaultValues, reviewSchema } from './constants';
import { FormProvider, useForm } from '@hooks/react-hook-form';
import useTncDialogHandler from './usecase/use-tnc-dialog-handler';
import { UserInformationReview } from 'pages/Home/NewApplication/constants';

import { reviewCx } from './styles';
import { useActionLog } from '@hooks/use-action-log';
import { callAllFn } from '@julofinance/web-helpers/dist/fn';

const Review = () => {
  const [, actions] = useGlobalState();
  const { onOpen } = useSnackbar();
  const { pushEvent } = useActionLog({ page: 'app_form_review_page' });

  const form = useForm<FormParam<'review'>>({
    defaultValues,
    shouldFocusError: false,
    resolver: yupResolver(reviewSchema),
  });

  const { handleSubmit, setValue, reset } = form;

  const { mutate } = useRSubmitApplication({
    onMutate: () => {
      actions.openLoadingOverlay();
    },
    onSettled: () => {
      actions.closeLoadingOverlay();
    },
    onSuccess: () => {
      window.location.replace('/home');
    },
    onError: (error) => {
      const statusCode = error.payload?.statusCode;
      const errorMessage =
        (error.payload?.errors as Array<string>)[0] ??
        'Oops..., something went wrong';

      switch (statusCode) {
        case 400:
          onOpen({ message: errorMessage, severity: 'error' });
          break;

        default:
          onOpen({ message: errorMessage, severity: 'error' });
          break;
      }
    },
  });
  const {
    onOpen: onTncDialogOpen,
    isOpen: isTncDialogOpen,
    onRejectTnc,
    onAcceptTnc,
  } = useTncDialogHandler({ onReject: () => setValue('privacyPolicy', false) });

  function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.checked) {
      pushEvent({
        element: 'app_form_review_page_tnc',
        event: 'click',
      });
      onTncDialogOpen();
    } else {
      onRejectTnc();
    }
  }

  function handleConsent(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.checked) {
      pushEvent({
        element: 'agree_app_form_review_page_consent',
        event: 'click',
      });
    }
  }

  function onSubmit(data: FormParam<'review'>) {
    pushEvent({
      element: 'user_submit_application',
      event: 'click',
    });
    mutate({
      variables: excludeKey<SavedApplicationDataResponse<'review'>>(
        data as SavedApplicationDataResponse<'review'>,
        [
          'privacyPolicy',
          'validData',
          'ktp',
          'selfieKtp',
          'currentStep',
          'referralCode',
        ], // exclude referralCode for now
      ),
    });
  }

  useRGetSavedApplication<SavedApplicationDataResponse<'review'>>({
    step: 4,
    onSuccess: ({ data }) => {
      reset(data);
    },
  });

  return (
    <FormProvider scrollToFieldOnError {...form}>
      {!isTncDialogOpen ? (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cx(reviewCx, 'review')}
        >
          {UserInformationReview.map(({ id, title, children }) => (
            <ReviewAccordion key={id} title={title}>
              {children}
            </ReviewAccordion>
          ))}

          <div className='actions'>
            <div className='agreement'>
              <Checkbox
                onChange={handleCheckboxChange}
                name='privacyPolicy'
                label='Saya telah membaca dan menyetujui Syarat & Ketentuan dan Kebijakan Privasi JULO'
              />
              <Checkbox
                name='validData'
                onChange={handleConsent}
                label='Saya setuju dengan pemeriksaan dan validasi data pribadi saya.'
              />
            </div>

            <ApplicationCard>
              <Button type='submit' fullWidth>
                Kirim
              </Button>
            </ApplicationCard>
          </div>
        </form>
      ) : (
        <TncDialog
          onAcceptTnc={callAllFn(onAcceptTnc, () =>
            pushEvent({
              element: 'agree_app_form_review_page_tnc',
              event: 'click',
            }),
          )}
          onRejectTnc={onRejectTnc}
        />
      )}
    </FormProvider>
  );
};

export default memo(Review);
